import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { LlmPricingRow, LlmUsageApiService } from 'src/app/core/services/llm-usage-api.service';
import { RulesApiService } from 'src/app/core/services/rules-api.service';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { ACTION_BUTTON } from 'src/app/shared/utils/action-button.classes';

interface ModelOption {
  id: number;
  label: string;
}

/**
 * The price list every LLM call is costed against.
 *
 * Each model may have at most one current price row. To change a price, edit it; to replace it
 * entirely, delete the row and add a new one. Prices are per MILLION tokens — the unit
 * providers publish.
 */
@Component({
  selector: 'app-llm-pricing',
  standalone: true,
  imports: [CommonModule, FormsModule, PageSurfaceHeaderComponent],
  templateUrl: './llm-pricing.component.html',
})
export class LlmPricingComponent implements OnInit {
  readonly btn = ACTION_BUTTON;
  private readonly api = inject(LlmUsageApiService);
  private readonly rulesApi = inject(RulesApiService);

  rows: LlmPricingRow[] = [];
  models: ModelOption[] = [];
  loading = false;
  saving = false;
  deletingId: number | null = null;
  error = '';
  showForm = false;
  editingId: number | null = null;

  form = {
    model_id: null as number | null,
    input_price_per_1m: '',
    output_price_per_1m: '',
    cached_input_price_per_1m: '',
    effective_from: new Date().toISOString().slice(0, 10),
    notes: '',
  };

  ngOnInit(): void {
    this.load();
    this.rulesApi.listLlmModels().subscribe({
      next: (res) => {
        this.models = (res.data ?? []).map((row) => ({
          id: Number(row['id']),
          label: `${row['provider']} / ${row['model_name']}`,
        }));
      },
      error: () => (this.models = []),
    });
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .listPricing()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => (this.rows = res.data ?? []),
        error: (err) => (this.error = resolveApiErrorMessage(err) || 'Could not load LLM pricing.'),
      });
  }

  /** Models that do not yet have a current price row — the only ones "Add price" may target. */
  get modelsAvailableForAdd(): ModelOption[] {
    const priced = new Set(
      this.rows.filter((row) => this.isCurrent(row)).map((row) => row.model_id),
    );
    return this.models.filter((model) => !priced.has(model.id));
  }

  modelLabel(modelId: number): string {
    return this.models.find((model) => model.id === modelId)?.label ?? `Model ${modelId}`;
  }

  openForm(): void {
    if (!this.modelsAvailableForAdd.length) {
      this.error = 'Every model already has a price. Edit an existing row or delete one before adding another.';
      return;
    }
    this.editingId = null;
    this.resetForm();
    this.showForm = true;
    this.error = '';
  }

  openEdit(row: LlmPricingRow): void {
    if (!this.isCurrent(row)) return;
    this.editingId = row.id;
    this.form = {
      model_id: row.model_id,
      input_price_per_1m: row.input_price_per_1m,
      output_price_per_1m: row.output_price_per_1m,
      cached_input_price_per_1m: row.cached_input_price_per_1m ?? '',
      effective_from: row.effective_from.slice(0, 10),
      notes: row.notes ?? '',
    };
    this.showForm = true;
    this.error = '';
  }

  closeForm(): void {
    this.showForm = false;
    this.editingId = null;
  }

  save(): void {
    if (!this.form.model_id || !this.form.input_price_per_1m || !this.form.output_price_per_1m) {
      this.error = 'Model, input price and output price are required.';
      return;
    }

    if (!this.editingId && this.rows.some((row) => this.isCurrent(row) && row.model_id === this.form.model_id)) {
      this.error = 'This model already has a price. Edit the existing row or delete it first.';
      return;
    }

    const body: Record<string, string | number> = {
      input_price_per_1m: this.form.input_price_per_1m,
      output_price_per_1m: this.form.output_price_per_1m,
    };
    if (this.form.cached_input_price_per_1m) {
      body['cached_input_price_per_1m'] = this.form.cached_input_price_per_1m;
    }
    if (this.form.notes) body['notes'] = this.form.notes;

    this.saving = true;
    this.error = '';
    const request = this.editingId
      ? this.api.updatePricing(this.editingId, body)
      : this.api.createPricing({
          model_id: this.form.model_id,
          ...body,
          effective_from: new Date(this.form.effective_from).toISOString(),
        });

    request.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => {
        this.showForm = false;
        this.editingId = null;
        this.load();
      },
      error: (err) => (this.error = resolveApiErrorMessage(err) || 'Could not save the price.'),
    });
  }

  deleteRow(row: LlmPricingRow): void {
    if (!this.isCurrent(row)) return;
    const label = row.model?.display_name ?? this.modelLabel(row.model_id);
    if (!confirm(`Delete the current price for ${label}? You can add a new price afterwards.`)) return;

    this.deletingId = row.id;
    this.error = '';
    this.api
      .deletePricing(row.id)
      .pipe(finalize(() => (this.deletingId = null)))
      .subscribe({
        next: () => this.load(),
        error: (err) => (this.error = resolveApiErrorMessage(err) || 'Could not delete the price.'),
      });
  }

  isCurrent(row: LlmPricingRow): boolean {
    return row.effective_to === null;
  }

  private resetForm(): void {
    this.form = {
      model_id: null,
      input_price_per_1m: '',
      output_price_per_1m: '',
      cached_input_price_per_1m: '',
      effective_from: new Date().toISOString().slice(0, 10),
      notes: '',
    };
  }
}
